import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormContext, Flex } from 'dash-ui';

import { useGetOrganizationsForSelectQuery } from 'gql/generated.graphql';
import { FormFields } from 'containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/constants/constants';
import useValidation from 'containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/hooks/useValidation';

const PrimaryOrganizationSelect = () => {
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const { formErrors, formState, stateHandlers } = useContext(FormContext);
  const valid = useValidation();

  const { data: result, loading } = useGetOrganizationsForSelectQuery({
    variables: { pagination: { allowLimit: false } },
  });
  const { data } = useMemo(
    () => ({
      data: result?.getOrganizations?.collection || [],
    }),
    [result]
  );

  const handleSelectInputs = (fieldName: FormFields) => (value: string) => {
    const prevOrgId = formState[FormFields.PrimaryOrganization];
    const prevSaleTeams = formState[FormFields.SaleTeams] || [];

    valid(fieldName, value);
    stateHandlers[fieldName](value);

    // Remove prev internal sale team from property
    if (prevSaleTeams.length && prevOrgId && prevOrgId !== value) {
      const { internalSaleTeam } =
        data.find((doc) => doc._id === prevOrgId) || {};
      const internalSaleTeamIdToRemove = internalSaleTeam?._id;

      if (internalSaleTeamIdToRemove) {
        const newSaleTeams = prevSaleTeams.filter(
          ({ saleTeamId }: any) => saleTeamId !== internalSaleTeamIdToRemove
        );

        stateHandlers[FormFields.SaleTeams](
          newSaleTeams.length ? newSaleTeams : [{}]
        );
      }
    }

    // Remove new primary organization from additional organizations
    stateHandlers[FormFields.Organizations](
      formState[FormFields.Organizations]?.filter((id: string) => id !== value)
    );
  };

  return (
    <Flex flexDirection="column" mr={2} flex={1}>
      <Form.Label>{t('forms:labels.primaryOrganization')}</Form.Label>
      <Form.Select
        menuPortalZIndex="auto"
        isLoading={loading}
        value={formState[FormFields.PrimaryOrganization]}
        options={data.map((item) => ({
          value: item._id,
          label: item.name,
        }))}
        placeholder={t('forms:placeholders.selectOne')}
        w="100%"
        onChanged={handleSelectInputs(FormFields.PrimaryOrganization)}
        $errorType={formErrors[FormFields.PrimaryOrganization]?.type}
      />
      <Form.Feedback
        $errorType={formErrors[FormFields.PrimaryOrganization]?.type}
      >
        {formErrors[FormFields.PrimaryOrganization]?.message}
      </Form.Feedback>
    </Flex>
  );
};

export default PrimaryOrganizationSelect;
